import { format } from 'date-fns'
import { CalendarIcon, X } from 'lucide-react'
import { DateRange } from 'react-day-picker'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useState } from 'react'

type Props = {
  className?: string
  date?: DateRange
  setDate?: (date: DateRange | undefined) => void
  isClearable?: boolean
}

export function DateRangePicker({
  className,
  date,
  setDate,
  isClearable,
}: Props) {
  const [pendingDate, setPendingDate] = useState<DateRange | undefined>(date)

  const handleSelect = (selectedDate: DateRange | undefined) => {
    if (
      selectedDate?.from &&
      selectedDate?.to &&
      selectedDate.from < selectedDate.to
    ) {
      setDate?.(selectedDate)
      setPendingDate(selectedDate)
    } else {
      setPendingDate(selectedDate)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDate?.(undefined)
    setPendingDate(undefined)
  }

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant={'outline'}
            className={cn(
              'w-44 justify-start text-left flex items-center gap-2 font-normal hover:bg-card hover:text-foreground',
              !date && 'text-muted-foreground hover:text-muted-foreground',
              isClearable && date && 'pr-2',
            )}
          >
            <CalendarIcon size={16} />
            <span className='flex-1'>
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, 'LLL dd')} - {format(date.to, 'LLL dd')}
                  </>
                ) : (
                  format(date.from, 'LLL dd')
                )
              ) : (
                <span>Custom range</span>
              )}
            </span>
            {isClearable && date && (
              <button
                type='button'
                className='ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2'
                onMouseDown={handleClear}
                onClick={handleClear}
              >
                <X className='opacity-50 hover:opacity-100 transition-opacity p-1' />
              </button>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='range'
            required={false}
            defaultMonth={pendingDate?.from}
            selected={pendingDate}
            onSelect={handleSelect}
            numberOfMonths={2}
            pagedNavigation={true}
            captionLayout='dropdown-years'
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
